import { useOptionMetaVendorDeviceSelection } from '@/delivery-type-plugin/components';

import { isFieldInteractionLocked } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../platform/capability-registry/shouldSkipCapabilityFetch';
import { BillingNodeSelector } from './components/BillingNodeSelector';
import { HiddenNodeFields } from './components/HiddenNodeFields';
import { PhysicalNodeFilters } from './components/PhysicalNodeFilters';
import { PhysicalNodeList } from './components/PhysicalNodeList';
import { BARE_METAL_NODES_FIELD_KEYS, BARE_METAL_NODES_OPTION_META_KEYS } from './fieldKeys';
import { usePhysicalNodeList } from './hooks/usePhysicalNodeList';
import type { BareMetalNodesBlockProps, BareMetalNodesPaths } from './types';

/** 裸金属节点能力块：计价模式选厂商设备，非计价模式选物理节点 */
export function BareMetalNodesBlock(props: BareMetalNodesBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as BareMetalNodesPaths;

  const isEdit = options?.isEdit === true;
  const billModelId = options?.billModelId;
  const deploymentTypeId = options?.deploymentTypeId;
  const disabled = isFieldInteractionLocked(BARE_METAL_NODES_FIELD_KEYS.bmsNode, control) || isEdit;

  const skipListFetch =
    options?.resourceEditMode === 'order-fixed' ||
    shouldSkipCapabilityFetch(control, {
      skipFetch: options?.skipFetch,
      fieldKeys: [BARE_METAL_NODES_FIELD_KEYS.bmsNode],
    });

  const [vendorDeviceSelection, setVendorDeviceSelection] = useOptionMetaVendorDeviceSelection(
    form,
    BARE_METAL_NODES_OPTION_META_KEYS.node,
  );
  const physicalNodeList = usePhysicalNodeList({
    form,
    paths,
    billModelId,
    isEdit,
    initBmsNode: options?.initData?.bmsNode,
    skipListFetch,
  });

  if (!isFieldVisible(BARE_METAL_NODES_FIELD_KEYS.bmsNode, control)) {
    return null;
  }

  return (
    <>
      {billModelId ? (
        <BillingNodeSelector
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          form={form}
          paths={paths}
          disabled={disabled}
          skipFetch={skipListFetch}
          value={vendorDeviceSelection}
          onSelectionChange={setVendorDeviceSelection}
          options={options}
        />
      ) : (
        <>
          <PhysicalNodeFilters
            disabled={disabled}
            archTypes={physicalNodeList.archTypes}
            queryObj={physicalNodeList.queryObj}
            onArchChange={(value) => physicalNodeList.updateFilter('arch', value)}
            onCpuChange={(value) => physicalNodeList.updateFilter('cpu', value)}
            onMemoryChange={(value) => physicalNodeList.updateFilter('memory', value)}
            onGpuChange={(value) => physicalNodeList.updateFilter('gpu', value)}
            onNameSearch={physicalNodeList.searchByName}
            onRefresh={physicalNodeList.refresh}
          />
          <PhysicalNodeList
            form={form}
            paths={paths}
            disabled={disabled}
            nodes={physicalNodeList.nodes}
            loading={physicalNodeList.loading}
            selectedNode={physicalNodeList.selectedNode}
            pagination={physicalNodeList.pagination}
            onSelectNode={physicalNodeList.setSelectedNode}
            onPageChange={physicalNodeList.changePage}
            options={options}
          />
        </>
      )}
      <HiddenNodeFields paths={paths} />
    </>
  );
}
